import { useMemo } from 'react';

import {
  FinanceRate,
  Job,
  Lead,
  Payment,
} from '@kanda-libs/ks-frontend-services';

import { useCurrentJob, useCurrentJobLead } from 'hooks';
import { FormattedJob } from 'types';
import { formatToCurrency } from 'utils';

import { AIP_APPROVED, AIP_DECLINED, SECOND_LINE_MESSAGE } from './constants';

import { getDepositPayments, getLoanPayments } from '../helpers';

export interface SelectNewRateHook {
  message: string;
}

interface AppliedValues {
  appliedRate?: FinanceRate;
  appliedLoan?: Payment;
  appliedDeposit?: Payment;
}

const getAppliedValues = (job?: FormattedJob | Job): AppliedValues => {
  if (!job)
    return {
      appliedRate: undefined,
      appliedLoan: undefined,
      appliedDeposit: undefined,
    };

  const rate = job?.customer_options?.finance_option;

  const loanPayments = getLoanPayments(job);
  const depositPayments = getDepositPayments(job);

  const loan = loanPayments?.[0];
  const deposit = depositPayments?.[0];

  return {
    appliedRate: rate,
    appliedLoan: loan,
    appliedDeposit: deposit,
  };
};

const getMaxEtikaCreditLine = (lead?: Lead): string => {
  if (!lead) return '£0';
  const etikaRateLines = lead.finance_options
    ?.filter((rate: FinanceRate) => rate.enabled && rate.provider === 'etika')
    ?.map((rate: FinanceRate) => rate?.credit_line || 0)
    ?.filter((line: number) => line > 0);
  if (!etikaRateLines || etikaRateLines?.length === 0) return '£0';
  return formatToCurrency(Math.max(...etikaRateLines)).slice(0, -3);
};

export default function useSelectNewRate(
  secondLine = false,
): SelectNewRateHook {
  const { job } = useCurrentJob();
  const { lead } = useCurrentJobLead();

  const { appliedRate, appliedLoan } = getAppliedValues(job);

  const maxCreditLine = getMaxEtikaCreditLine(lead);

  const loan = formatToCurrency(
    appliedLoan?.payment_option?.amount?.amount || 0,
  ).slice(0, -3);

  const apr = `${(appliedRate?.apr || 0) / 100}% APR`;

  const months = `${appliedRate?.duration || 0} months`;

  const hasBeenPreApproved = [
    'issued',
    'open',
    'trade_notified',
    'trade_accepted',
  ].includes(lead?.status || '');

  const message = useMemo(() => {
    if (secondLine) {
      return SECOND_LINE_MESSAGE.replace('$loan', loan)
        .replace('$apr', apr)
        .replace('$months', months);
    }
    if (!hasBeenPreApproved)
      return AIP_DECLINED.replace('$loan', loan)
        .replace('$apr', apr)
        .replace('$months', months)
        .replace('$maxLine', maxCreditLine);
    return AIP_APPROVED.replace('$loan', loan)
      .replace('$apr', apr)
      .replace('$months', months)
      .replace('$maxLine', maxCreditLine);
  }, [hasBeenPreApproved, maxCreditLine, loan, apr, months, secondLine]);

  return {
    message,
  };
}
